import React, { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { useForm } from 'react-hook-form'
import { AxiosError } from 'axios'
import { showBanner } from '../../global-state/banner-slice'
import { faqServices, flattenFaqs } from '../../services/faq-service'
import { FaqFormPresenter } from './faq-form-presenter'

export type FaqFormValues = {
  question: string
  answer: string
  category: string
  sort_order: number
}

const defaultValues: FaqFormValues = {
  question: '',
  answer: '',
  category: '',
  sort_order: 0,
}

const toPayload = (data: FaqFormValues) => ({
  question: data.question.trim(),
  answer: data.answer,
  category: data.category.trim() === '' ? null : data.category.trim(),
  sort_order: Number.isFinite(data.sort_order) ? data.sort_order : 0,
})

export function FaqFormContainer() {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { id } = useParams()
  const isEditMode = Boolean(id)

  const form = useForm<FaqFormValues>({
    defaultValues,
    mode: 'onBlur',
  })

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    if (!id) return
    const load = async () => {
      try {
        setIsLoading(true)
        const data = await faqServices.list()
        const row = flattenFaqs(data).find((r) => r.id === Number(id))
        if (!row) {
          dispatch(showBanner({ text: 'FAQ not found', variant: 'error-banner' }))
          navigate('/faqs')
          return
        }
        form.reset({
          question: row.question,
          answer: row.answer,
          category: row.category ?? '',
          sort_order: row.sort_order,
        })
      } catch {
        dispatch(showBanner({ text: 'Failed to load FAQ', variant: 'error-banner' }))
        navigate('/faqs')
      } finally {
        setIsLoading(false)
      }
    }
    void load()
  }, [id, form, dispatch, navigate])

  const onSubmit = async (data: FaqFormValues) => {
    const payload = toPayload(data)
    try {
      setIsSubmitting(true)
      if (isEditMode && id) {
        await faqServices.update(Number(id), payload)
        dispatch(showBanner({ text: 'FAQ updated', variant: 'success-banner' }))
      } else {
        await faqServices.create(payload)
        dispatch(showBanner({ text: 'FAQ created', variant: 'success-banner' }))
      }
      navigate('/faqs')
    } catch (err: unknown) {
      const message =
        err instanceof AxiosError &&
        err.response?.data &&
        typeof (err.response.data as { message?: unknown }).message === 'string'
          ? (err.response.data as { message: string }).message
          : 'Save failed'
      dispatch(showBanner({ text: message, variant: 'error-banner' }))
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <FaqFormPresenter
      form={form}
      isEditMode={isEditMode}
      isSubmitting={isSubmitting}
      isLoading={isLoading}
      onSubmit={onSubmit}
      onCancel={() => navigate('/faqs')}
    />
  )
}
